import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const fmtDate = (d) =>
  new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

// Manual operating-hours meter entries + trend. Operators can log readings;
// admins/techs can also delete erroneous entries.
const OperatingHoursCard = ({ machineryId, canLog, isAdmin, onChanged }) => {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('operatingHoursCollapsed') === '1'; } catch { return false; }
  });
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/machinery/${machineryId}/hours`);
      setData(res.data);
    } catch { /* non-fatal */ }
  }, [machineryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('operatingHoursCollapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  const handleLog = async (e) => {
    e.preventDefault();
    const val = parseFloat(hours);
    if (isNaN(val) || val < 0) {
      toast.error('Enter a valid meter reading');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/machinery/${machineryId}/hours`, {
        hours: val,
        note: note.trim() || null,
      });
      toast.success(`Reading logged: ${val.toLocaleString()} hrs`);
      setHours('');
      setNote('');
      await fetchData();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log reading');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/machinery-hours/${entryId}`);
      toast.success('Entry deleted');
      await fetchData();
      if (onChanged) onChanged();
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const latest = data?.latest_hours;

  return (
    <Card data-testid="operating-hours-card">
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center justify-between gap-2 w-full text-left"
          data-testid="operating-hours-toggle"
          aria-expanded={!collapsed}
        >
          <span className="flex items-center gap-2">
            <span className="text-slate-400 text-sm w-4">{collapsed ? '▸' : '▾'}</span>
            <CardTitle className="text-base">Operating Hours</CardTitle>
          </span>
          {latest != null && (
            <span className="text-sm font-semibold text-slate-700" data-testid="operating-hours-summary">
              {Number(latest).toLocaleString()} hrs
            </span>
          )}
        </button>
      </CardHeader>
      {!collapsed && (
        <CardContent className="space-y-4" data-testid="operating-hours-body">
          {latest != null ? (
            <div>
              <div className="text-3xl font-bold text-slate-900" data-testid="operating-hours-latest">
                {Number(latest).toLocaleString()} <span className="text-base font-medium text-slate-500">hrs</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                as of {fmtDate(data.latest_recorded_at)}
              </div>
              {data.avg_hours_per_day != null && (
                <div className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200" data-testid="operating-hours-rate">
                  Trend: ~{data.avg_hours_per_day} hrs/day
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">
              No readings yet. Log the current meter reading to start tracking hours-based PM schedules.
            </div>
          )}

          {canLog && (
            <form onSubmit={handleLog} className="space-y-2 border-t pt-3">
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Meter reading (hrs)"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  data-testid="operating-hours-input"
                />
                <Button type="submit" disabled={saving || hours === ''} data-testid="operating-hours-log-btn">
                  {saving ? 'Saving…' : 'Log'}
                </Button>
              </div>
              <Input
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="text-sm"
                data-testid="operating-hours-note-input"
              />
            </form>
          )}

          {(data?.entries?.length || 0) > 0 && (
            <details className="border-t pt-3">
              <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700" data-testid="operating-hours-history-toggle">
                Reading history ({data.entry_count})
              </summary>
              <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto">
                {data.entries.slice(0, 30).map((en) => (
                  <div key={en.id} className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1.5" data-testid={`hours-entry-${en.id}`}>
                    <div className="min-w-0">
                      <span className="font-semibold">{Number(en.hours).toLocaleString()} hrs</span>
                      <span className="text-slate-500 ml-2">{fmtDate(en.recorded_at)}</span>
                      <div className="text-slate-500 truncate">
                        by {en.recorded_by_name}{en.note ? ` · ${en.note}` : ''}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700 shrink-0"
                        onClick={() => handleDelete(en.id)}
                        title="Delete entry"
                        data-testid={`delete-hours-entry-${en.id}`}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default OperatingHoursCard;
